/*
 * Copyright 2024, Corning Optical Communications
 * All Rights Reserved.
 */

/**
 * MA3000 Hierarchy Builder
 * Parses MA3000 DAS inventory data and builds the topology hierarchy
 * 
 * ObjectKey Format: {controllerSerial}.{deviceType}.{deviceSerial}
 * Topology Format: {chassisIndex}.{slot}.{port}.{remote}
 */

import { IInventoryGraphNode, IRemoteEquipment } from "../../../store/api/devices/types";
import { TopologyNode, DasNodeType, DasNodeStatus, HierarchyBuilder } from "../types";
import { EQUIPMENT_TYPE_MAP } from "../constants";

interface ObjectKeyParts {
  siteSn: string;
  deviceType?: string;
  chassisIndex?: number;
  slot?: number;
  port?: number;
  remote?: number;
}

export class MA3000HierarchyBuilder implements HierarchyBuilder {
  private equipmentMap: Map<string, IRemoteEquipment>;

  constructor() {
    this.equipmentMap = new Map();
  }

  /**
   * Set equipment data for additional details
   */
  setEquipmentData(equipment: IRemoteEquipment[]) {
    this.equipmentMap.clear();
    
    console.log('[MA3000] Setting equipment data:', equipment.length, 'items');
    const sampleEquipment = equipment.slice(0, 3);
    console.log('[MA3000] Sample equipment (first 3):', sampleEquipment.map(e => ({
      objectKey: e.objectKey,
      equipmentType: e.equipmentType,
      hostname: e.hostname
    })));
    
    equipment.forEach((eq) => {
      // Index by objectKey (primary)
      if (eq.objectKey) {
        this.equipmentMap.set(eq.objectKey, eq);
      }
      // Also index by hostname for fallback
      if (eq.hostname) {
        this.equipmentMap.set(eq.hostname, eq);
      }
    });
    
    console.log('[MA3000] Equipment map size:', this.equipmentMap.size);
  }

  /**
   * Extract RIM band from equipment data
   * Checks both the direct band field and rimBand in attributes
   */
  private extractRimBand(equipment: IRemoteEquipment | undefined): string {
    if (!equipment) return '';
    
    // Check direct band field first
    if (equipment.band) {
      return equipment.band;
    }
    
    // Parse attributes to get rimBand or band
    if (equipment.attributes) {
      try {
        let attrs: any;
        if (typeof equipment.attributes === 'string') {
          let attrString = equipment.attributes.trim();
          // Handle potential malformed JSON
          if (attrString.startsWith('{') && !attrString.endsWith('}')) {
            attrString += '}';
          }
          attrs = JSON.parse(attrString);
        } else {
          attrs = equipment.attributes;
        }
        
        // Try rimBand first, then band
        if (attrs.rimBand) {
          return attrs.rimBand;
        }
        if (attrs.band) {
          return attrs.band;
        }
      } catch (e) {
        // Ignore parse errors
      }
    }
    
    return '';
  }

  /**
   * Get equipment details for a node
   */
  private getEquipment(node: IInventoryGraphNode): IRemoteEquipment | undefined {
    // Try multiple lookup strategies
    let equipment = this.equipmentMap.get(node.id);
    if (equipment) return equipment;
    
    equipment = this.equipmentMap.get(node.label);
    if (equipment) return equipment;
    
    // For OCH nodes, search by objectKey pattern match
    // Support both old format (underscore) and new format (dot-separated)
    if (node.equipmentType === 'OCH' || node.id.includes('_') || node.id.includes('.')) {
      const equipmentList = Array.from(this.equipmentMap.values());
      for (const eq of equipmentList) {
        if (eq.equipmentType === 'OCH' && eq.objectKey && node.id.includes(eq.objectKey)) {
          return eq;
        }
      }
    }
    
    // For ACM nodes specifically, try finding by equipment type and objectKey pattern
    if (node.equipmentType === 'ACM') {
      // Search through all equipment for matching ACM
      const equipmentList = Array.from(this.equipmentMap.values());
      for (const eq of equipmentList) {
        if (eq.equipmentType === 'ACM' && eq.objectKey) {
          // Check if this ACM belongs to the same chassis as the node
          // For ACM, the label is typically like "HEU-1", extract the number
          if (node.label && eq.objectKey.includes(node.label.split('-')[0])) {
            return eq;
          }
        }
      }
    }
    
    return undefined;
  }

  /**
   * Determine chassis type from ACM chassisType field
   * Values: heu (1), oiu (2), ihu (3)
   */
  private determineChassisType(chassisKey: string, acmNode?: IInventoryGraphNode): DasNodeType {
    let acmEquipment: IRemoteEquipment | undefined;
    
    // Strategy 1: If we have an ACM node, try to look it up
    if (acmNode) {
      acmEquipment = this.getEquipment(acmNode);
    }
    
    // Strategy 2: Search for ACM with matching objectKey
    if (!acmEquipment) {
      const equipmentList = Array.from(this.equipmentMap.values());
      
      for (const eq of equipmentList) {
        // Since objectKey is underscore-separated, convert chassisKey for comparison
        if (eq.equipmentType === 'ACM' && eq.objectKey) {
          const parts = this.parseObjectKey(eq.objectKey);
          const eqChassisKey = `${parts.siteSn}.${parts.chassisIndex}`;
          if (eqChassisKey === chassisKey) {
            acmEquipment = eq;
            break;
          }
        }
      }
      
      if (!acmEquipment) {
        // Try partial match - ACM objectKey might have additional trailing parts
        for (const eq of equipmentList) {
          if (eq.equipmentType === 'ACM' && eq.objectKey) {
            const parts = this.parseObjectKey(eq.objectKey);
            const eqChassisKey = `${parts.siteSn}.${parts.chassisIndex}`;
            if (eqChassisKey.startsWith(chassisKey)) {
              acmEquipment = eq;
              break;
            }
          }
        }
      }
    }
    
    if (!acmEquipment?.attributes) {
      return DasNodeType.HEU;
    }

    // Parse attributes to get chassis type
    try {
      let attrs: any;
      if (typeof acmEquipment.attributes === 'string') {
        // Try to fix common JSON issues
        let attrString = acmEquipment.attributes.trim();
        
        // Decode HTML entities first (backend returns &quot; instead of ")
        attrString = attrString
          .replace(/&quot;/g, '"')
          .replace(/&amp;/g, '&')
          .replace(/&lt;/g, '<')
          .replace(/&gt;/g, '>')
          .replace(/&#39;/g, "'");
        
        // Replace single quotes with double quotes (if still needed)
        if (attrString.includes("'") && !attrString.includes('"')) {
          attrString = attrString.replace(/'/g, '"');
        }
        
        // Remove trailing commas before closing braces/brackets
        attrString = attrString.replace(/,(\s*[}\]])/g, '$1');
        
        try {
          attrs = JSON.parse(attrString);
        } catch (parseError) {
          // Try to extract chassisType/acmChassisType using regex as last resort
          const chassisTypeMatch = attrString.match(/["']?(?:acm)?[Cc]hassisType["']?\s*[:=]\s*["']?(\d+|[a-zA-Z]+)["']?/);
          if (chassisTypeMatch) {
            const extractedType = chassisTypeMatch[1];
            attrs = { acmChassisType: extractedType };
          } else {
            return DasNodeType.HEU;
          }
        }
      } else if (typeof acmEquipment.attributes === 'object') {
        // Already an object
        attrs = acmEquipment.attributes;
      } else {
        return DasNodeType.HEU;
      }
      
      const chassisType = attrs.chassisType || attrs.acmChassisType;

      // Map chassis type value to DasNodeType
      switch (chassisType) {
        case 1:
        case '1':
        case 'heu':
        case 'HEU':
          return DasNodeType.HEU;
        case 2:
        case '2':
        case 'oiu':
        case 'OIU':
          return DasNodeType.OIU;
        case 3:
        case '3':
        case 'ihu':
        case 'IHU':
          return DasNodeType.IHU;
        default:
          return DasNodeType.HEU; // Fallback
      }
    } catch (e) {
      return DasNodeType.HEU;
    }
  }

  /**
   * Parse objectKey string into components
    * objectKey: {controllerSerial}.{deviceType}.{deviceSerial}
    * topology: {chassisIndex}.{slot}.{port}.{remote}
   */
  private parseObjectKey(objectKey: string): ObjectKeyParts {
    if (!objectKey) {
      return {
        siteSn: '',
      };
    }

    // ObjectKey uses dot separator
    const parts = objectKey.split(".");
    
    const result: ObjectKeyParts = {
      siteSn: parts[0] || '',
    };

    if (parts.length > 1) result.deviceType = parts[1];

    const equipment = this.equipmentMap.get(objectKey);
    const topology = equipment?.topology;
    if (topology) {
      const topo = topology.split('.');
      // Pad to 4 parts with '0' so downstream code always gets chassis.slot.port.remote
      while (topo.length < 4) topo.push('0');
      result.chassisIndex = parseInt(topo[0], 10);
      result.slot = parseInt(topo[1], 10);
      result.port = parseInt(topo[2], 10);
      result.remote = parseInt(topo[3], 10);
    }

    return result;
  }

  /**
   * Parse node type from equipment type
   */
  parseNodeType(node: IInventoryGraphNode): DasNodeType {
    const equipmentType = node.equipmentType?.toUpperCase() || "";
    
    // Map equipment type to DAS node type
    if (EQUIPMENT_TYPE_MAP[equipmentType]) {
      return EQUIPMENT_TYPE_MAP[equipmentType];
    }

    // Default to generic type based on context
    return DasNodeType.SITE;
  }

  /**
   * Parse status from API status string
   */
  parseStatus(status?: string): DasNodeStatus {
    const statusLower = (status || "").toLowerCase();
    
    switch (statusLower) {
      case "online":
      case "normal":
        return DasNodeStatus.ONLINE;
      case "clear":
      case "cleared":
        return DasNodeStatus.CLEAR;
      case "minor":
        return DasNodeStatus.MINOR;
      case "major":
      case "warning":
        return DasNodeStatus.MAJOR;
      case "critical":
      case "error":
        return DasNodeStatus.CRITICAL;
      case "disconnected":
      case "offline":
        return DasNodeStatus.DISCONNECTED;
      default:
        return DasNodeStatus.UNKNOWN;
    }
  }

  /**
   * Create topology node from graph node
   */
  private createTopologyNode(node: IInventoryGraphNode, type: DasNodeType): TopologyNode {
    const equipment = this.getEquipment(node);
    
    // Fix equipment type if it starts with "-" (e.g., "-3000" should be "MA-3000")
    let equipmentType = node.equipmentType || "";
    if (equipmentType.startsWith("-")) {
      equipmentType = "MA" + equipmentType;
    }
    
    // Generate clean label from equipment type and objectKey index
    let label = '';
    
    // For SITE type (root), always show "MA-3000"
    if (type === DasNodeType.SITE) {
      label = 'MA-3000';
      equipmentType = 'MA-3000';  // Ensure equipment type is also consistent
    } else {
      const objectKey = equipment?.objectKey || "";
      
      if (objectKey && equipmentType) {
        // Use parseObjectKey to handle underscore format properly
        const parts = this.parseObjectKey(objectKey);
        let index = '';
        
        // Determine which part to use as index
        if (parts.remote !== undefined && parts.remote !== 0) {
          index = parts.remote.toString(); // RXU index
        } else if (parts.port !== undefined && parts.port !== 0) {
          index = parts.port.toString(); // Remote port index (RAU/MRU)
        } else if (parts.slot !== undefined && parts.slot !== 0) {
          index = parts.slot.toString(); // Module slot index (RIM/OIM/FMM)
        } else if (parts.chassisIndex !== undefined && parts.chassisIndex !== 0) {
          index = parts.chassisIndex.toString(); // Chassis index (not used here, chassis labels are set elsewhere)
        }
        
        // Create clean label
        if (index) {
          // For RIM modules, include the band in the label: "RIM-LTE-5"
          if (equipmentType === 'RIM') {
            const band = this.extractRimBand(equipment);
            label = band ? `RIM-${band}-${index}` : `RIM-${index}`;
          } else {
            // Other modules: "OIM-3", "RAU-5", "RXU-2", etc.
            label = `${equipmentType}-${index}`;
          }
        } else {
          // Fallback: just equipment type (shouldn't normally happen)
          label = equipmentType;
        }
      } else {
        // No objectKey or equipmentType - use fallback
        label = equipmentType || node.label || node.id;
      }
    }
    
    return {
      id: node.id,
      label,
      type,
      equipmentType,
      status: this.parseStatus(node.status || equipment?.status),
      model: node.model || equipment?.model,
      ipAddress: node.ipAddress || equipment?.ipAddress,
      managedObject: equipment?.objectKey,
      children: [],
      metadata: {
        serialNumber: equipment?.serialNumber,
        equipment,
        rawNode: node,
      },
    };
  }

  /**
   * Build MA3000 hierarchy from flat node list
   * Hierarchy:
   * - Site (root)
   *   - HEU (chassis) - grouped by chassisSerial.chassisIndex
   *     - ACM (slot 0)
   *     - Modules (slots 1-12: RIM, OIM, FMM, etc.)
   *       - Remotes (RAU, MRU) - identified by port index
   *         - RXU - identified by rxu index
   */
  buildHierarchy(nodes: IInventoryGraphNode[], _edges: any[], rootId: string): TopologyNode | null {
    if (nodes.length === 0) {
      return null;
    }

    // Find root node (site)
    const rootNode = nodes.find((n) => n.id === rootId);
    if (!rootNode) {
      return null;
    }

    const root = this.createTopologyNode(rootNode, DasNodeType.SITE);
    
    // Override root equipment type to be "MA-3000" if not already set correctly
    if (!root.equipmentType || root.equipmentType === "-3000" || !root.equipmentType.startsWith("MA")) {
      root.equipmentType = "MA-3000";
    }

    // Group nodes by chassis using managedObject from equipment data
    const chassisMap = new Map<string, IInventoryGraphNode[]>();
    const ochChassisMap = new Map<string, IInventoryGraphNode[]>(); // Separate map for OCH
    const nonChassisNodes: IInventoryGraphNode[] = [];

    console.log('[MA3000] Starting chassis grouping for', nodes.length, 'nodes');
    
    // Log equipment types in nodes array
    const equipmentTypeCounts = new Map<string, number>();
    nodes.forEach(node => {
      const type = node.equipmentType || 'UNKNOWN';
      equipmentTypeCounts.set(type, (equipmentTypeCounts.get(type) || 0) + 1);
    });
    console.log('[MA3000] Equipment type counts in nodes array:', Object.fromEntries(equipmentTypeCounts));
    
    // Specifically check for MXU, FCM, FRM
    const mxuNodes = nodes.filter(n => n.equipmentType?.toUpperCase() === 'MXU');
    const fcmNodes = nodes.filter(n => n.equipmentType?.toUpperCase() === 'FCM');
    const frmNodes = nodes.filter(n => n.equipmentType?.toUpperCase() === 'FRM');
    console.log('[MA3000] MXU nodes:', mxuNodes.length);
    console.log('[MA3000] FCM nodes:', fcmNodes.length);
    console.log('[MA3000] FRM nodes:', frmNodes.length);
    if (frmNodes.length > 0) {
      console.log('[MA3000] FRM node details:', frmNodes.map(n => ({
        id: n.id,
        label: n.label,
        equipmentType: n.equipmentType,
        model: n.model
      })));
    }

    nodes.forEach((node, index) => {
      if (node.id === rootId) return; // Skip root

      const equipment = this.getEquipment(node);
      const objectKey = equipment?.objectKey || "";
      
      if (index < 5) {
        console.log(`[MA3000] Node ${index}:`, {
          nodeId: node.id,
          equipmentType: node.equipmentType,
          equipment: equipment ? {
            objectKey: equipment.objectKey
          } : 'NOT_FOUND',
          objectKey: objectKey
        });
      }
      
      if (!objectKey) {
        if (index < 5) console.log(`[MA3000] Node ${index} -> nonChassisNodes (no objectKey)`);
        nonChassisNodes.push(node);
        return;
      }

      // HCM is the head unit controller - it's the MA-3000 device itself, not a chassis
      // Skip HCM from chassis grouping
      if (node.equipmentType?.toUpperCase() === "HCM") {
        if (index < 5) console.log(`[MA3000] Node ${index} -> Skip HCM (head unit, not a chassis)`);
        return;
      }

      const parts = this.parseObjectKey(objectKey);
      if (index < 5) {
        console.log(`[MA3000] Node ${index} parsed parts:`, parts);
      }
      
      // Chassis is identified by siteSn.chassisIndex combination
      // This separates HEU-1, HEU-2, OIU-5, etc. into different groups
      if (parts.siteSn && parts.chassisIndex !== undefined) {
        const chassisKey = `${parts.siteSn}.${parts.chassisIndex}`;
        
        if (index < 5) {
          console.log(`[MA3000] Node ${index} -> chassisKey: ${chassisKey}`);
        }
        
        // OCH nodes go to separate map
        if (node.equipmentType?.toUpperCase() === "OCH" || node.equipmentType?.toUpperCase() === "GX") {
          if (!ochChassisMap.has(chassisKey)) {
            ochChassisMap.set(chassisKey, []);
          }
          ochChassisMap.get(chassisKey)!.push(node);
        } else {
          // Regular chassis nodes (HEU/OIU/IHU) - only ACM-based chassis
          if (!chassisMap.has(chassisKey)) {
            chassisMap.set(chassisKey, []);
          }
          chassisMap.get(chassisKey)!.push(node);
        }
      } else {
        if (index < 5) console.log(`[MA3000] Node ${index} -> nonChassisNodes (no chassisSerial or chassisIndex)`);
        nonChassisNodes.push(node);
      }
    });

    console.log('[MA3000] Chassis grouping complete:');
    console.log('  - chassisMap keys:', Array.from(chassisMap.keys()));
    console.log('  - ochChassisMap keys:', Array.from(ochChassisMap.keys()));
    console.log('  - nonChassisNodes:', nonChassisNodes.length);
    
    // Log detailed chassis grouping info
    chassisMap.forEach((nodes, key) => {
      const equipmentTypes = new Map<string, number>();
      nodes.forEach(n => {
        const type = n.equipmentType || 'UNKNOWN';
        equipmentTypes.set(type, (equipmentTypes.get(type) || 0) + 1);
      });
      console.log(`  - Chassis ${key}: ${nodes.length} nodes -`, Object.fromEntries(equipmentTypes));
    });

    // First, collect all ACMs by chassisIndex (not chassisKey)
    // ACMs may have different chassisSerial than their modules (simulator issue)
    const acmByChassisIndex = new Map<number, { node: IInventoryGraphNode; equipment: IRemoteEquipment }>();
    
    chassisMap.forEach((chassisNodes) => {
      chassisNodes.forEach((node) => {
        if (node.equipmentType?.toUpperCase() === "ACM") {
          const equipment = this.getEquipment(node);
          if (equipment?.objectKey) {
            const parts = this.parseObjectKey(equipment.objectKey);
            if (parts.chassisIndex !== undefined) {
              acmByChassisIndex.set(parts.chassisIndex, { node, equipment });
              console.log(`[MA3000] Found ACM for chassis index ${parts.chassisIndex}: ${equipment.objectKey}`);
            }
          }
        }
      });
    });
    
    console.log(`[MA3000] Total ACMs found: ${acmByChassisIndex.size}`);

    // Build chassis (HEU/OIU/IHU) nodes
    // Group by chassisIndex instead of chassisKey to handle simulator serial mismatches
    const chassisByIndex = new Map<number, { key: string; nodes: IInventoryGraphNode[] }>();
    
    chassisMap.forEach((chassisNodes, chassisKey) => {
      // Include all chassis, even if they only contain ACM (empty chassis should be displayed)
      const hasNonACM = chassisNodes.some(n => n.equipmentType?.toUpperCase() !== "ACM");
      if (!hasNonACM) {
        console.log(`[MA3000] Processing ACM-only chassis: ${chassisKey}`);
      }
      
      // Extract chassisIndex from any node in this group
      const firstNode = chassisNodes[0];
      const firstEquipment = this.getEquipment(firstNode);
      if (!firstEquipment?.objectKey) return;
      
      const parts = this.parseObjectKey(firstEquipment.objectKey);
      if (parts.chassisIndex !== undefined) {
        if (!chassisByIndex.has(parts.chassisIndex)) {
          chassisByIndex.set(parts.chassisIndex, { key: chassisKey, nodes: [] });
        }
        chassisByIndex.get(parts.chassisIndex)!.nodes.push(...chassisNodes);
      }
    });
    
    console.log(`[MA3000] Chassis groups by index: ${chassisByIndex.size}`);
    
    chassisByIndex.forEach(({ key: chassisKey, nodes: chassisNodes }, chassisIndexNum) => {
      // Find ACM for this chassis index
      const acmData = acmByChassisIndex.get(chassisIndexNum);

      if (!acmData) {
        console.log(`[MA3000] Warning: No ACM found for chassis index ${chassisIndexNum}, skipping`);
        return;
      }
      
      const { node: acmNode, equipment: acmEquipment } = acmData;
      const chassisIndex = chassisIndexNum.toString();
      
      // Determine chassis type (HEU/OIU/IHU) from ACM's acmChassisType attribute
      const chassisType = this.determineChassisType(chassisKey, acmNode);
      const chassisLabel = chassisType.toUpperCase() + '-' + chassisIndex;
      
      console.log(`[MA3000] Creating chassis: ${chassisLabel} (index=${chassisIndex}, type=${chassisType}, ${chassisNodes.length} nodes)`);
      
      // Use the ACM node itself as the chassis (not a container)
      // The ACM IS the chassis, not a module inside a chassis container
      // Display it as HEU/OIU/IHU type, but it's still the ACM device
      const heuNode: TopologyNode = {
        id: acmNode.id, // Use ACM's original ID
        label: chassisLabel, // Display as HEU-1, OIU-5, etc.
        type: chassisType, // HEU/OIU/IHU based on acmChassisType
        equipmentType: chassisType.toUpperCase(), // Display as HEU/OIU/IHU, not ACM
        status: this.parseStatus(acmNode.status || acmEquipment?.status),
        model: acmNode.model || acmEquipment?.model,
        ipAddress: acmNode.ipAddress || acmEquipment?.ipAddress,
        serialNumber: acmEquipment?.serialNumber,
        managedObject: acmEquipment?.objectKey || chassisKey,
        children: [],
        metadata: {
          chassisKey,
          chassisIndex: chassisIndexNum,
          isACM: true,
          actualEquipmentType: 'ACM', // Store original type for drawer
          equipment: acmEquipment,
          rawNode: acmNode,
        },
      };

      // Group remaining nodes by slot (modules - only non-OCH/GX)
      const moduleMap = new Map<number, IInventoryGraphNode>();

      chassisNodes.forEach((node) => {
        const equipment = this.getEquipment(node);
        if (!equipment?.objectKey) return;
        
        const parts = this.parseObjectKey(equipment.objectKey);
        
        if (parts.slot === 0) return; // ACM already added

        const nodeEquipmentType = node.equipmentType?.toUpperCase() || "";
        
        // Skip OCH and GX - they are handled separately
        if (nodeEquipmentType === "OCH" || nodeEquipmentType === "GX") {
          return;
        }

        // Regular modules (has slot > 0, port = 0)
        // Modules have slot > 0 and port === 0
        if (parts.slot && parts.slot > 0 && parts.port === 0) {
          moduleMap.set(parts.slot, node);
        }
      });

      // Add modules (sorted by slot)
      const sortedSlots = Array.from(moduleMap.keys()).sort((a, b) => a - b);
      
      console.log(`[MA3000] Chassis ${chassisKey} (${chassisLabel}): Building ${sortedSlots.length} modules`);
      
      sortedSlots.forEach((slot) => {
        const moduleNode = moduleMap.get(slot)!;
        const moduleType = this.parseNodeType(moduleNode);
        const moduleTopologyNode = this.createTopologyNode(moduleNode, moduleType);

        const moduleEquipment = this.getEquipment(moduleNode);
        if (!moduleEquipment?.objectKey) {
          heuNode.children.push(moduleTopologyNode);
          return;
        }
        
        const moduleParts = this.parseObjectKey(moduleEquipment.objectKey);
        
        // Find remotes that belong to this module (RAU and MRU both have slot+port)
        const remoteCount = { RAU: 0, MRU: 0 };
        
        chassisNodes.forEach((node) => {
          const equipment = this.getEquipment(node);
          if (!equipment?.objectKey) return;
          
          const parts = this.parseObjectKey(equipment.objectKey);
          
          const nodeEquipmentType = node.equipmentType?.toUpperCase() || "";
          
          // Skip OCH and GX - they are handled separately
          if (nodeEquipmentType === "OCH" || nodeEquipmentType === "GX") {
            return;
          }
          
          // Remote (RAU or MRU) belongs to this module if it has same slot and has port > 0 and remote = 0
          // Remotes have port > 0 and remote === 0
          if (parts.slot === moduleParts.slot && parts.port && parts.port > 0 && parts.remote === 0) {
            const remoteType = this.parseNodeType(node);
            const remoteTopologyNode = this.createTopologyNode(node, remoteType);
            
            if (nodeEquipmentType === "RAU") remoteCount.RAU++;
            if (nodeEquipmentType === "MRU") remoteCount.MRU++;

            // Find RXU children for RAU, or MXU + RXU children for MRU
            let rxuCount = 0;
            let mxuCount = 0;
            
            chassisNodes.forEach((childNode) => {
              const childEquipment = this.getEquipment(childNode);
              if (!childEquipment?.objectKey) return;
              
              const childParts = this.parseObjectKey(childEquipment.objectKey);
              const childEquipmentType = childNode.equipmentType?.toUpperCase() || "";
              
              // RXU belongs to RAU if it matches slot.port and has remote > 0
              if (
                nodeEquipmentType === "RAU" &&
                childEquipmentType === "RXU" &&
                childParts.slot === parts.slot &&
                childParts.port === parts.port &&
                childParts.remote &&
                childParts.remote > 0
              ) {
                const childType = this.parseNodeType(childNode);
                const childTopologyNode = this.createTopologyNode(childNode, childType);
                remoteTopologyNode.children.push(childTopologyNode);
                rxuCount++;
              }
              
              // MXU belongs to MRU if it matches slot.port and has remote > 0
              if (
                nodeEquipmentType === "MRU" &&
                childEquipmentType === "MXU" &&
                childParts.slot === parts.slot &&
                childParts.port === parts.port &&
                childParts.remote &&
                childParts.remote > 0
              ) {
                const childType = this.parseNodeType(childNode);
                const childTopologyNode = this.createTopologyNode(childNode, childType);
                remoteTopologyNode.children.push(childTopologyNode);
                mxuCount++;
                console.log(`[MA3000] Added MXU child to MRU: ${childNode.id} (slot=${childParts.slot}, port=${childParts.port}, remote=${childParts.remote})`);
              }
              
              // RXU can also belong to MRU (MRU can have both MXU and RXU children)
              if (
                nodeEquipmentType === "MRU" &&
                childEquipmentType === "RXU" &&
                childParts.slot === parts.slot &&
                childParts.port === parts.port &&
                childParts.remote &&
                childParts.remote > 0
              ) {
                const childType = this.parseNodeType(childNode);
                const childTopologyNode = this.createTopologyNode(childNode, childType);
                remoteTopologyNode.children.push(childTopologyNode);
                rxuCount++;
              }
            });
            
            if (nodeEquipmentType === "RAU" && rxuCount > 0) {
              console.log(`[MA3000] RAU ${node.id} has ${rxuCount} RXU children`);
            }
            if (nodeEquipmentType === "MRU" && (mxuCount > 0 || rxuCount > 0)) {
              console.log(`[MA3000] MRU ${node.id} (slot=${parts.slot}, port=${parts.port}) has ${mxuCount} MXU + ${rxuCount} RXU children`);
            }

            moduleTopologyNode.children.push(remoteTopologyNode);
          }
        });
        
        console.log(`[MA3000] Module slot ${slot}: ${remoteCount.RAU} RAU, ${remoteCount.MRU} MRU`);

        heuNode.children.push(moduleTopologyNode);
      });

      // Add chassis to topology (including empty chassis with only ACM)
      root.children.push(heuNode);
      if (heuNode.children.length === 0) {
        console.log(`[MA3000] Added empty chassis (ACM-only): ${chassisLabel}`);
      }
    });

    // Build OCH chassis nodes (separate from HEU/OIU/IHU)
    ochChassisMap.forEach((ochNodes, chassisKey) => {
      // Parse chassis key to get chassis index
      const keyParts = this.parseObjectKey(chassisKey);
      const chassisIndex = keyParts.chassisIndex || 0;
      
      // Group OCH nodes by port (not slot) - port determines the group
      const portMap = new Map<number, IInventoryGraphNode[]>();
      
      ochNodes.forEach((node, idx) => {
        const equipment = this.getEquipment(node);
        if (!equipment?.objectKey) return;
        
        const parts = this.parseObjectKey(equipment.objectKey);
        // New format: all parts always defined, port will be a number (0 or greater)
        if (parts.port !== undefined && parts.port !== null) {
          if (!portMap.has(parts.port)) {
            portMap.set(parts.port, []);
          }
          portMap.get(parts.port)!.push(node);
        }
      });
      
      // Create OCH chassis node with chassis index
      const firstOch = ochNodes[0];
      const ochChassisNode: TopologyNode = {
        id: `och-chassis-${chassisKey}`,
        label: `OCH-${chassisIndex}`,
        type: DasNodeType.OCH,
        equipmentType: "OCH",
        status: this.parseStatus(firstOch.status),
        managedObject: chassisKey,
        children: [],
        metadata: {
          chassisKey,
          isOchChassis: true,
        },
      };
      
      // Add port groups as children
      // The last number in managedObject determines the label:
      // ends with .1.1 → OCH(1-4)
      // ends with .1.2 → OCH(5-8)
      const sortedPorts = Array.from(portMap.keys()).sort((a, b) => a - b);
      
      sortedPorts.forEach((port) => {
        const portNodes = portMap.get(port);
        if (!portNodes || portNodes.length === 0) return;
        
        // Find the node that actually has this port number in its objectKey
        let selectedNode = portNodes[0];
        let selectedEquipment = this.getEquipment(selectedNode);
        
        for (const node of portNodes) {
          const equipment = this.getEquipment(node);
          if (equipment?.objectKey) {
            const parts = this.parseObjectKey(equipment.objectKey);
            if (parts.port === port) {
              selectedNode = node;
              selectedEquipment = equipment;
              break;
            }
          }
        }
        
        // Determine label based on port number (last digit in managedObject)
        const portRange = port === 1 ? "1-4" : "5-8";
        const baseLabel = `OCH ${portRange}`; // Space instead of parentheses
        const deviceName = selectedEquipment?.hostname || selectedNode.label;
        
        // Create port group node using actual equipment data
        // Use equipment objectKey or hostname as ID for proper drawer linking
        const nodeId = selectedEquipment?.objectKey || selectedEquipment?.hostname || selectedNode.id;
        
        const portGroupNode: TopologyNode = {
          id: nodeId, // Use equipment identifier for proper drawer linking
          label: baseLabel, // Just "OCH 5-8" for inside the box
          type: DasNodeType.OCH,
          equipmentType: "OCH",
          status: this.parseStatus(selectedNode.status || selectedEquipment?.status),
          model: selectedNode.model || selectedEquipment?.model,
          ipAddress: selectedNode.ipAddress || selectedEquipment?.ipAddress,
          managedObject: selectedEquipment?.objectKey, // Use objectKey for topology identification
          serialNumber: selectedEquipment?.serialNumber,
          children: [],
          metadata: {
            port,
            portRange,
            deviceName, // Store device name for external display
            equipment: selectedEquipment,
            rawNode: selectedNode,
          },
        };
        
        ochChassisNode.children.push(portGroupNode);
      });
      
      root.children.push(ochChassisNode);
    });

    return root;
  }
}

// Export singleton instance
export const ma3000HierarchyBuilder = new MA3000HierarchyBuilder();
